import type {
  IDraftItem,
  IMenuDraft,
  IMenuImportPage,
  TReviewLevel,
} from '@/interfaces/IMenuImport';
import { orderedItems } from './menu-draft';
import { groupOwnerPages } from './owner-pages';

/**
 * «Carta original» (plan §4.5.5): the photos of the menu with a box over
 * every dish read from them. Boxes are `[ymin, xmin, ymax, xmax]` in 0–1000
 * of the page image (§5.3); they are drawn in % so they follow any zoom.
 */

/**
 * Mirror of the API's `MENU_IMPORT_BOXES_ENABLED` (§8.8, set by the bench).
 * When the API turns boxes off it stores none, so no overlay is drawn; this
 * switch also hides the boxes of drafts read before that.
 */
export const MENU_IMPORT_BOXES_ENABLED = true;

export const VIEWER_ZOOM_MIN = 1;
export const VIEWER_ZOOM_MAX = 3;
export const VIEWER_ZOOM_STEP = 0.5;

/** One step closer («+»), never beyond 3×. */
export function zoomIn(zoom: number): number {
  return Math.min(VIEWER_ZOOM_MAX, zoom + VIEWER_ZOOM_STEP);
}

/** One step back («−»), never below 1×. */
export function zoomOut(zoom: number): number {
  return Math.max(VIEWER_ZOOM_MIN, zoom - VIEWER_ZOOM_STEP);
}

/**
 * The pages the viewer can show: photos (and PDF pages) with a stored image,
 * in the order they were added. Pasted text and spreadsheets have no image.
 */
export function viewerPages(pages: IMenuImportPage[]): IMenuImportPage[] {
  return pages
    .filter((page) => page.kind === 'image' && page.storageKey !== null)
    .slice()
    .sort((a, b) => a.position - b.position);
}

/** Where a shown image is among the pages the owner added. */
export interface IViewerNumber {
  /** 1-based page as the owner added it. */
  page: number;
  /** 1-based part of a very tall page read in parts; null for a whole page. */
  part: number | null;
  /** How many pages the owner added. */
  total: number;
}

/**
 * The number of each image of `viewerPages` (same order): «2» for a whole
 * page, «2.3» for the third part of a very tall second page.
 */
export function viewerNumbers(images: IMenuImportPage[]): IViewerNumber[] {
  const groups = groupOwnerPages(images);
  return groups.flatMap((group, index) =>
    group.map((image) => ({
      page: index + 1,
      part: image.originPart,
      total: groups.length,
    }))
  );
}

/** The page to show: the one asked for, else the current one, else the first; null without pages. */
export function resolveViewerPage(
  pages: IMenuImportPage[],
  current: string | null,
  requested: string | null
): string | null {
  const has = (pageId: string | null) =>
    pageId !== null && pages.some((page) => page.pageId === pageId);
  if (has(requested)) return requested;
  if (has(current)) return current;
  return pages.length > 0 ? pages[0].pageId : null;
}

/** The first page of the viewer a dish was read from («Ver en la carta»); null for copied or typed dishes. */
export function itemSourcePageId(
  item: IDraftItem,
  pages: IMenuImportPage[]
): string | null {
  const shown = new Set(viewerPages(pages).map((page) => page.pageId));
  return item.sourceRefs.find((ref) => shown.has(ref.pageId))?.pageId ?? null;
}

export type TSourceBox = [number, number, number, number];

/** Four numbers in 0–1000 with a positive height and width. */
export function isValidSourceBox(box: unknown): box is TSourceBox {
  if (!Array.isArray(box) || box.length !== 4) return false;
  if (
    !box.every(
      (value) =>
        typeof value === 'number' &&
        Number.isFinite(value) &&
        value >= 0 &&
        value <= 1000
    )
  )
    return false;
  const [ymin, xmin, ymax, xmax] = box;
  return ymin < ymax && xmin < xmax;
}

/** A box in % of the page image. */
export interface IBoxRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function boxRect(box: TSourceBox): IBoxRect {
  const [ymin, xmin, ymax, xmax] = box;
  return {
    top: ymin / 10,
    left: xmin / 10,
    width: (xmax - xmin) / 10,
    height: (ymax - ymin) / 10,
  };
}

export interface IViewerOverlay {
  itemId: string;
  name: string;
  level: TReviewLevel;
  rect: IBoxRect;
}

/**
 * The boxes of one page: one per dish read from it (its first valid box on
 * that page), in menu order. None when boxes are off.
 */
export function pageOverlays(
  draft: IMenuDraft,
  pageId: string,
  enabled: boolean
): IViewerOverlay[] {
  if (!enabled) return [];
  const overlays: IViewerOverlay[] = [];
  for (const item of orderedItems(draft)) {
    const ref = item.sourceRefs.find(
      (candidate) =>
        candidate.pageId === pageId && isValidSourceBox(candidate.box)
    );
    if (!ref || !isValidSourceBox(ref.box)) continue;
    overlays.push({
      itemId: item.id,
      name: item.name,
      level: item.review.level,
      rect: boxRect(ref.box),
    });
  }
  return overlays;
}
